import API_BASE from "./apiBase";
import React, { useState } from "react";
import './Login.css';

/**
 * ===========================================================================
 * SIGN IN
 * ===========================================================================
 * Rewritten for the delivery API. The old one posted to `/api/login` and got
 * back a single token plus an organisation name and a dozen feature flags.
 *
 * THE NEW API ISSUES ONE TOKEN PER BRANCH. A user allocated Frome and Bath
 * gets two, and which one a request carries decides which branch's rows it can
 * touch. So a session is no longer "a token" — it is a set of them, plus a
 * current selection.
 *
 * WHAT IS STORED, and why the old key names survive:
 *
 *   authToken          the CURRENT branch's token — 58 reads across the app
 *   token              the same value; 20 more reads use this name instead
 *   tokenMap           every branch's token, keyed by account id
 *   members            the branch list, for the switcher
 *   organisationName   the GROUP name — 27 reads, and what the old fleet
 *                      fan-out keyed on
 *   currentAccountId   which branch `authToken` belongs to
 *
 * Renaming these would mean touching ~120 call sites across 40 screens for no
 * behavioural gain. The shapes changed; the names did not.
 *
 * ONE ASYMMETRY WORTH KNOWING: the head-office endpoints scope by USER, not by
 * the token's branch, so the fleet dashboard shows every branch the person can
 * see whichever token is current. Per-branch screens scope by token. That is
 * deliberate — a fleet view that changed depending on which branch happened to
 * be selected would be useless.
 */
const Login = ({ setToken, setUserData }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  // Shown only when the account covers more than one branch. A single-branch
  // user never sees it — asking a question with one answer is an extra click
  // and nothing else.
  const [accounts, setAccounts] = useState(null);
  const [pending, setPending] = useState(null);

  const handleLogin = async (e) => {
    if (e) e.preventDefault();
    setError(null);
    setBusy(true);

    try {
      const response = await fetch(`${API_BASE}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: username.trim(), password }),
      });

      const data = await response.json();

      if (!response.ok) {
        // The API distinguishes a wrong password from a locked account from
        // one with no branches allocated, and each needs a different action
        // from whoever reads it. Passing the server's own wording through is
        // more use than flattening all three into "login failed".
        setError((data && data.error && data.error.message) || "Sign in failed");
        return;
      }

      const list = data.accounts || [];
      if (!list.length) {
        setError("Your account is not linked to a branch yet. "
               + "Ask a manager in your group to give you access.");
        return;
      }

      if (list.length === 1) {
        finish(data, list[0]);
      } else {
        setPending(data);
        setAccounts(list);
      }
    } catch (err) {
      setError("Could not reach the server. Check your connection.");
    } finally {
      setBusy(false);
    }
  };

  const finish = (data, account) => {
    const tokenMap = {};
    for (const a of data.accounts) tokenMap[a.account_id] = a.token;

    // Both names, same value. Different screens read one or the other, and
    // reconciling that is not worth 78 edits.
    localStorage.setItem("authToken", account.token);
    localStorage.setItem("token", account.token);
    localStorage.setItem("tokenMap", JSON.stringify(tokenMap));

    localStorage.setItem("currentAccountId", account.account_id);
    localStorage.setItem("organisationName", account.group_name || "");
    localStorage.setItem("groupId", account.group_id || "");

    localStorage.setItem("userId", data.user ? data.user.id : "");
    localStorage.setItem("userName", data.user ? data.user.full_name : "");
    localStorage.setItem("userEmail", (data.user && data.user.email) || "");
    localStorage.setItem("roleKey",
      ((data.user && data.user.role) || "").toUpperCase());

    // The branch switcher reads this. `members` is the old name for the same
    // idea: the list of sites this person can open.
    localStorage.setItem("members", JSON.stringify(
      data.accounts.map(function (a) {
        return {
          id: a.account_id,
          name: a.account_name,
          group_id: a.group_id,
          group_name: a.group_name,
        };
      })
    ));

    setToken(account.token);
    if (setUserData) {
      setUserData({
        id: data.user ? data.user.id : null,
        name: data.user ? data.user.full_name : "",
        email: (data.user && data.user.email) || "",
        role: (data.user && data.user.role) || "",
        accountId: account.account_id,
        accountName: account.account_name,
        groupId: account.group_id,
        organisationName: account.group_name,
      });
    }
  };

  if (accounts) {
    return (
      <div className="login-container">
        <div className="login-box">
          <h2>Choose a branch</h2>
          <p style={{ fontSize: 13, color: "#6b7280", marginTop: -8 }}>
            You can switch later without signing out.
          </p>
          {accounts.map(function (a) {
            return (
              <button
                key={a.account_id}
                onClick={function () { finish(pending, a); }}
                className="login-button"
                style={{ marginTop: 8, textAlign: "left" }}
              >
                {a.account_name}
                <span style={{ opacity: 0.7, fontSize: 12 }}>
                  {" \u2014 "}{a.group_name}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <form className="login-box" onSubmit={handleLogin}>
        <h2>Sign in</h2>

        <label htmlFor="username">Username</label>
        <input
          id="username"
          type="text"
          value={username}
          autoComplete="username"
          autoCapitalize="none"
          autoCorrect="off"
          onChange={function (e) { setUsername(e.target.value); }}
          disabled={busy}
        />

        <label htmlFor="password">Password</label>
        <input
          id="password"
          type="password"
          value={password}
          autoComplete="current-password"
          onChange={function (e) { setPassword(e.target.value); }}
          disabled={busy}
        />

        {error && (
          <div style={{
            marginTop: 12, padding: 10, borderRadius: 6,
            background: "#fef2f2", border: "1px solid #fecaca",
            color: "#991b1b", fontSize: 13
          }}>
            {error}
          </div>
        )}

        <button type="submit" className="login-button" disabled={busy}>
          {busy ? "Signing in\u2026" : "Sign in"}
        </button>
      </form>
    </div>
  );
};

export default Login;
